/* eslint-disable no-console */
import { startAuthentication, startRegistration } from '@simplewebauthn/browser'
import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
} from '@simplewebauthn/browser'
import { userAuthHeader } from './apiClient'
import { PasskeyError, PasskeysUnavailableHereError } from './errors'
import {
  describeErrorBody,
  fetchOrThrowNetworkError,
  newRequestId,
  readErrorBody,
} from './http/utils'
import { HTTPStatus } from '~/api/httpStatus'
import { getPublicRuntimeConfig } from '~/cynqueModules/nuxtAccessors'
import {
  KNOWN_AUTH_METHODS,
  PASSKEY_PROBLEM_TYPES,
  type IPasskey,
  type TAuthMethod,
} from '~/types/passkey/models'
import type { IProblemDetails } from '~/types/errors/models'

// Passkey routes (cynque-api-server#1183). They go through `passkeyRequest`
// rather than httpGet/httpPost because every refusal has to be told apart by
// its problem-details `type`, and the shared verbs file a 403 or 503 under
// APIUnhandledStatus with the body buried. They are also never asynchronous: a
// ceremony cannot be replayed, so the api opts them out of 202 + polling.

const mn = '[services/passkeys.ts]'

const usersPath = '/api/users'
const currentPasskeysPath = `${usersPath}/current/passkeys`

const GENERIC_FAILURE =
  'Something went wrong with the passkey. Please try again.'

// User-facing copy for each refusal the api can give. `lastSignInMethod` is
// absent on purpose: the api's own `detail` for it is already written for the
// user ("This is your only way to sign in. Add another passkey…").
const MESSAGES: Record<string, string> = {
  [PASSKEY_PROBLEM_TYPES.invalidCredentials]:
    "We couldn't sign you in with that passkey. Try another, or ask your administrator for help.",
  [PASSKEY_PROBLEM_TYPES.registrationInvalid]:
    "The passkey couldn't be saved. Please try again.",
  [PASSKEY_PROBLEM_TYPES.alreadyRegistered]:
    'That passkey is already registered.',
  [PASSKEY_PROBLEM_TYPES.notFound]: 'That passkey no longer exists.',
  [PASSKEY_PROBLEM_TYPES.signInBusy]:
    'Too many sign-ins are in progress. Try again in a moment.',
}

/** Turn a refused passkey request into a PasskeyError carrying user-facing copy. */
export function passkeyErrorFromResponse(
  status: number,
  body: unknown
): PasskeyError {
  const problemType =
    body && typeof body === 'object'
      ? (body as Partial<IProblemDetails>).type
      : undefined
  const type = typeof problemType === 'string' ? problemType : undefined
  const message =
    (type && MESSAGES[type]) || describeErrorBody(body, GENERIC_FAILURE)
  return new PasskeyError(status, type, message)
}

async function passkeyRequest<T>(
  callingMn: string,
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
  apiPath: string,
  body?: unknown
): Promise<T> {
  const fn = `${callingMn} --> passkeyRequest |`
  const headers: Record<string, string> = Object.assign(
    {
      'x-request-id': newRequestId(),
      'x-app-version': getPublicRuntimeConfig().version,
      accept: 'application/json',
    },
    userAuthHeader()
  )
  if (body !== undefined) {
    headers['content-type'] = 'application/json'
  }
  console.debug(`${fn} ${method} ${apiPath}`)
  // Never log `body` or the response: a ceremony response and the sign-in
  // envelope both carry credentials.
  const response = await fetchOrThrowNetworkError(apiPath, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  })
  if (response.status === HTTPStatus.NoContent) {
    return undefined as T
  }
  if (!response.ok) {
    const errorBody = await readErrorBody(response)
    const error = passkeyErrorFromResponse(response.status, errorBody)
    console.log(
      `${fn} refused (${response.status} ${error.problemType ?? '-'}): ${
        error.message
      }`
    )
    throw error
  }
  const responseBody = await response.json()
  return responseBody.data as T
}

// -- sign-in methods ------------------------------------------------------------------

/**
 * The sign-in methods this deployment offers, in the api's order. A method this
 * webapp does not know (a future `oidc`) is dropped rather than rendered blank.
 */
export async function getAuthenticationMethods(): Promise<TAuthMethod[]> {
  const callingMn = `${mn} ${getAuthenticationMethods.name}`
  const methods = await passkeyRequest<unknown>(
    callingMn,
    'GET',
    `${usersPath}/authentication/methods`
  )
  if (!Array.isArray(methods)) {
    throw new PasskeyError(200, undefined, GENERIC_FAILURE)
  }
  return methods.filter((m): m is TAuthMethod =>
    KNOWN_AUTH_METHODS.includes(m as TAuthMethod)
  )
}

// -- sign in (no user yet) ------------------------------------------------------------

/**
 * Whether a ceremony for `rpId` can run on a page at `hostname`. The browser
 * requires the RP ID to be the host itself or a registrable suffix of it; it
 * would refuse with a SecurityError otherwise. An absent RP ID means "this
 * page's host", which always qualifies.
 */
export function rpIdCoversHost(
  rpId: string | undefined,
  hostname: string | undefined
): boolean {
  if (!rpId || !hostname) {
    return true
  }
  const host = hostname.toLowerCase()
  const rp = rpId.toLowerCase()
  return host === rp || host.endsWith(`.${rp}`)
}

function getAuthenticationOptions(callingMn: string) {
  return passkeyRequest<PublicKeyCredentialRequestOptionsJSON>(
    callingMn,
    'POST',
    `${usersPath}/passkeys/authentication/options`,
    {}
  )
}

/**
 * Whether a passkey sign-in could run on this page, without starting one: the
 * same RP ID check `signInWithPasskey` makes. Null when it could; otherwise the
 * error that sign-in would throw, whose `passkeyHost` says where passkeys do
 * work. The login page used to learn this from the autofill ceremony it started
 * on load; that ceremony now waits for the password form's Email field, so the
 * page asks up front instead. The options' challenge is left unused, to expire.
 */
export async function getPasskeysUnavailableHere(): Promise<PasskeysUnavailableHereError | null> {
  const callingMn = `${mn} ${getPasskeysUnavailableHere.name}`
  const optionsJSON = await getAuthenticationOptions(callingMn)
  return rpIdCoversHost(optionsJSON.rpId, globalThis.location?.hostname)
    ? null
    : new PasskeysUnavailableHereError(optionsJSON.rpId)
}

/**
 * Run a passkey sign-in and return the auth token, for `completeLogin`.
 *
 * `useBrowserAutofill` starts the conditional-UI ceremony: the browser offers
 * the passkey in the email field's autofill (`autocomplete="username webauthn"`)
 * and the promise stays pending until the user picks one. Starting a second
 * ceremony aborts it, which rejects it with ERROR_CEREMONY_ABORTED — expected,
 * and silenced by describeWebAuthnError.
 */
export async function signInWithPasskey({
  useBrowserAutofill = false,
}: { useBrowserAutofill?: boolean } = {}): Promise<string> {
  const callingMn = `${mn} ${signInWithPasskey.name}`
  const optionsJSON = await getAuthenticationOptions(callingMn)
  if (!rpIdCoversHost(optionsJSON.rpId, globalThis.location?.hostname)) {
    throw new PasskeysUnavailableHereError(optionsJSON.rpId)
  }
  const credential: AuthenticationResponseJSON = await startAuthentication({
    optionsJSON,
    useBrowserAutofill,
  })
  const envelope = await passkeyRequest<{ authToken: string }>(
    callingMn,
    'POST',
    `${usersPath}/passkeys/authentication`,
    credential
  )
  return envelope.authToken
}

// -- the signed-in user's passkeys ----------------------------------------------------

export async function getPasskeys(): Promise<IPasskey[]> {
  const callingMn = `${mn} ${getPasskeys.name}`
  return await passkeyRequest<IPasskey[]>(callingMn, 'GET', currentPasskeysPath)
}

/** Create a passkey on this device for the signed-in user, and register it. */
export async function registerPasskey(label?: string): Promise<IPasskey> {
  const callingMn = `${mn} ${registerPasskey.name}`
  const optionsJSON =
    await passkeyRequest<PublicKeyCredentialCreationOptionsJSON>(
      callingMn,
      'POST',
      `${currentPasskeysPath}/registration/options`,
      {}
    )
  if (!rpIdCoversHost(optionsJSON.rp?.id, globalThis.location?.hostname)) {
    throw new PasskeysUnavailableHereError(optionsJSON.rp?.id)
  }
  const credential: RegistrationResponseJSON = await startRegistration({
    optionsJSON,
  })
  const trimmed = label?.trim()
  return await passkeyRequest<IPasskey>(
    callingMn,
    'POST',
    `${currentPasskeysPath}/registration`,
    trimmed ? { label: trimmed, credential } : { credential }
  )
}

export async function renamePasskey(
  id: string,
  label: string
): Promise<IPasskey> {
  const callingMn = `${mn} ${renamePasskey.name}`
  return await passkeyRequest<IPasskey>(
    callingMn,
    'PATCH',
    `${currentPasskeysPath}/${encodeURIComponent(id)}`,
    { label }
  )
}

/**
 * Delete a passkey. The api ends the sessions it created (D12) and refuses
 * with `passkey/last_sign_in_method` (409) when it is the user's only way in.
 */
export async function deletePasskey(id: string): Promise<void> {
  const callingMn = `${mn} ${deletePasskey.name}`
  await passkeyRequest<void>(
    callingMn,
    'DELETE',
    `${currentPasskeysPath}/${encodeURIComponent(id)}`
  )
}
